import React, { useState, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Paper,
  Chip,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ListItemIcon,
  Snackbar,
  Alert,
  CircularProgress,
  Alert as MuiAlert,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  Stack,
  Fab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import GrainIcon from '@mui/icons-material/Grain';
import HistoryIcon from '@mui/icons-material/History';
import { useFoodHistory, FoodEntry } from '../context/FoodHistoryContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const Nutrition: React.FC = () => {
  const { history, searchByFood, getStats, loading, error, refreshHistory } = useFoodHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<FoodEntry | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [showShareMessage, setShowShareMessage] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [dateFilter, setDateFilter] = useState<'7days' | '30days' | '60days' | 'all'>('7days');

  const filteredHistory = useMemo(() => {
    let filtered = history;
    
    // Filtrar por búsqueda de texto
    if (searchQuery) {
      filtered = searchByFood(searchQuery);
    }
    
    // Filtrar por fecha
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    
    switch (dateFilter) {
      case '7days':
        filtered = filtered.filter(entry => new Date(entry.date) >= sevenDaysAgo);
        break;
      case '30days':
        filtered = filtered.filter(entry => new Date(entry.date) >= thirtyDaysAgo);
        break;
      case '60days':
        filtered = filtered.filter(entry => new Date(entry.date) >= sixtyDaysAgo);
        break;
      case 'all':
      default:
        break;
    }
    
    return filtered;
  }, [history, searchQuery, searchByFood, dateFilter]);

  const stats = useMemo(() => getStats(), [getStats]);

  // Ordenar por fecha descendente
  const sortedHistory = [...filteredHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Función para capitalizar la primera letra
  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Función para abrir el modal de detalle
  const handleOpenDetail = (entry: FoodEntry) => {
    setSelectedEntry(entry);
    setIsDetailModalOpen(true);
  };

  // Función para cerrar el modal de detalle
  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedEntry(null);
  };

  // Función para abrir el modal de historial
  const handleOpenHistory = () => {
    setIsHistoryModalOpen(true);
  };

  // Función para cerrar el modal de historial
  const handleCloseHistory = () => {
    setIsHistoryModalOpen(false);
  };

  // Función para compartir
  const handleShare = async () => {
    if (!selectedEntry) return;

    const shareText = `🍽️ Mi plato saludable del ${new Date(selectedEntry.date).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })}

🥗 Alimentos: ${selectedEntry.foods.map(food => capitalizeFirstLetter(food)).join(', ')}

📊 Nutrición: ${selectedEntry.nutritionInfo ? `${selectedEntry.nutritionInfo.calories} kcal, ${selectedEntry.nutritionInfo.protein}g proteínas` : 'Información no disponible'}

#GreenBalance #Saludable #Nutrición`;

    // Detectar si es móvil y tiene Web Share API
    if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: 'Mi plato saludable - GreenBalance',
          text: shareText,
          url: window.location.href
        });
      } catch (error) {
        console.log('Error al compartir:', error);
        // Fallback: copiar al portapapeles
        copyToClipboard(shareText);
      }
    } else {
      // En desktop, copiar al portapapeles
      copyToClipboard(shareText);
    }
  };

  // Función para copiar al portapapeles
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowShareMessage(true);
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error);
      // Fallback para navegadores antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowShareMessage(true);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="sm" sx={{ px: 2 }}>
      <Box sx={{ py: 2 }}>
        {/* Título compacto */}
        <Typography variant="h5" component="h1" sx={{ 
          fontWeight: 'bold', 
          color: '#2e7d32',
          textAlign: 'center',
          mb: 2
        }}>
          Historial de Platos
        </Typography>

        {/* Mostrar error si existe */}
        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        {/* Carrusel de platos - Muestra 2.5 platos */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            position: 'relative'
          }}>
            {sortedHistory.slice(0, 5).map((entry) => (
              <Card key={entry.id} sx={{ 
                minWidth: '45%',
                borderRadius: 2, 
                boxShadow: 2,
                '&:hover': { 
                  transform: 'scale(1.02)', 
                  transition: 'transform 0.2s',
                  boxShadow: 4
                }
              }}>
                <CardActionArea onClick={() => handleOpenDetail(entry)}>
                  {entry.imageUrl && (
                    <Box sx={{ width: '100%', aspectRatio: '1/1', overflow: 'hidden', bgcolor: '#f4faef' }}>
                      <img
                        src={entry.imageUrl}
                        alt="Plato registrado"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  )}
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                      {new Date(entry.date).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </Typography>
                    {entry.nutritionInfo && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        {entry.nutritionInfo.calories} kcal
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
            {/* Indicador de scroll */}
            {sortedHistory.length > 2 && (
              <Box sx={{ 
                position: 'absolute', 
                right: 8, 
                top: '50%', 
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.2rem',
                zIndex: 1
              }}>
                →
              </Box>
            )}
          </Box>
          {/* Indicador de scroll debajo */}
          {sortedHistory.length > 2 && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                ← Desliza para ver más platos →
              </Typography>
            </Box>
          )}
        </Box>

        {/* Botón Ver Historial */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleOpenHistory}
            startIcon={<HistoryIcon />}
            sx={{ 
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 'bold'
            }}
          >
            Ver Historial Completo
          </Button>
        </Box>

        {/* Buscador compacto - Solo filtros de días */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>
            Filtrar por período:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={dateFilter === '7days' ? 'contained' : 'outlined'}
              onClick={() => setDateFilter('7days')}
              size="small"
              sx={{ fontSize: '0.7rem', px: 1 }}
            >
              7 días
            </Button>
            <Button
              variant={dateFilter === '30days' ? 'contained' : 'outlined'}
              onClick={() => setDateFilter('30days')}
              size="small"
              sx={{ fontSize: '0.7rem', px: 1 }}
            >
              30 días
            </Button>
            <Button
              variant={dateFilter === '60days' ? 'contained' : 'outlined'}
              onClick={() => setDateFilter('60days')}
              size="small"
              sx={{ fontSize: '0.7rem', px: 1 }}
            >
              60 días
            </Button>
            <Button
              variant={dateFilter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setDateFilter('all')}
              size="small"
              sx={{ fontSize: '0.7rem', px: 1 }}
            >
              Todo
            </Button>
          </Box>
        </Paper>

        {/* Resumen y Nutrición con Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '0.9rem'
              }
            }}
          >
            <Tab label="Resumen" />
            <Tab label="Nutrición" />
          </Tabs>
          
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Card sx={{ 
                bgcolor: '#e3f2fd', 
                borderRadius: 2, 
                textAlign: 'center',
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                    <RestaurantIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {stats.totalEntries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Platos
                  </Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ 
                bgcolor: '#f3e5f5', 
                borderRadius: 2, 
                textAlign: 'center',
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Avatar sx={{ bgcolor: '#9c27b0', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                    <GrainIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {stats.uniqueFoods.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Alimentos
                  </Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ 
                bgcolor: '#e8f5e8', 
                borderRadius: 2, 
                textAlign: 'center',
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                    <TrendingUpIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {stats.averageFoodsPerDay.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Promedio
                  </Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ 
                bgcolor: '#fff3e0', 
                borderRadius: 2, 
                textAlign: 'center',
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                    <LocalFireDepartmentIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {stats.nutritionStats.averageCalories.toFixed(0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Calorías
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Card sx={{ 
                bgcolor: '#e8f5e8', 
                borderRadius: 2,
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocalFireDepartmentIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Calorías
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                    {stats.nutritionStats.averageCalories.toFixed(0)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.nutritionStats.averageCalories / 800) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </CardContent>
              </Card>
              
              <Card sx={{ 
                bgcolor: '#e3f2fd', 
                borderRadius: 2,
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FitnessCenterIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Proteínas
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                    {stats.nutritionStats.averageProtein.toFixed(1)}g
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.nutritionStats.averageProtein / 25) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </CardContent>
              </Card>
              
              <Card sx={{ 
                bgcolor: '#fff3e0', 
                borderRadius: 2,
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GrainIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Carbohidratos
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                    {stats.nutritionStats.averageCarbs.toFixed(1)}g
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.nutritionStats.averageCarbs / 50) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </CardContent>
              </Card>
              
              <Card sx={{ 
                bgcolor: '#f3e5f5', 
                borderRadius: 2,
                boxShadow: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Fibra
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                    {stats.nutritionStats.averageFiber.toFixed(1)}g
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.nutritionStats.averageFiber / 10) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </CardContent>
              </Card>
            </Box>
          </TabPanel>
        </Box>

        {/* Estado de carga */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Mensaje cuando no hay datos */}
        {!loading && sortedHistory.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No hay platos registrados aún
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ve a la página de registro para añadir tu primer plato
            </Typography>
          </Box>
        )}
      </Box>

      {/* Modal de Historial Completo */}
      <Dialog 
        open={isHistoryModalOpen} 
        onClose={handleCloseHistory}
        fullScreen
        PaperProps={{
          sx: { borderRadius: 0 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Historial Completo
          </Typography>
          <IconButton onClick={handleCloseHistory} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          {/* Buscador en el modal */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar alimentos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                      <SearchIcon color="primary" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={dateFilter === '7days' ? 'contained' : 'outlined'}
                  onClick={() => setDateFilter('7days')}
                  size="small"
                  sx={{ fontSize: '0.7rem', px: 1 }}
                >
                  7 días
                </Button>
                <Button
                  variant={dateFilter === '30days' ? 'contained' : 'outlined'}
                  onClick={() => setDateFilter('30days')}
                  size="small"
                  sx={{ fontSize: '0.7rem', px: 1 }}
                >
                  30 días
                </Button>
                <Button
                  variant={dateFilter === '60days' ? 'contained' : 'outlined'}
                  onClick={() => setDateFilter('60days')}
                  size="small"
                  sx={{ fontSize: '0.7rem', px: 1 }}
                >
                  60 días
                </Button>
                <Button
                  variant={dateFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setDateFilter('all')}
                  size="small"
                  sx={{ fontSize: '0.7rem', px: 1 }}
                >
                  Todo
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Galería de 3 columnas */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 2 
          }}>
            {sortedHistory.map((entry) => (
              <Card key={entry.id} sx={{ 
                borderRadius: 2, 
                boxShadow: 2,
                '&:hover': { 
                  transform: 'scale(1.02)', 
                  transition: 'transform 0.2s',
                  boxShadow: 4
                }
              }}>
                <CardActionArea onClick={() => handleOpenDetail(entry)}>
                  {entry.imageUrl && (
                    <Box sx={{ width: '100%', aspectRatio: '1/1', overflow: 'hidden', bgcolor: '#f4faef' }}>
                      <img
                        src={entry.imageUrl}
                        alt="Plato registrado"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  )}
                  <CardContent sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>
                      {new Date(entry.date).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short'
                      })}
            </Typography>
                    {entry.nutritionInfo && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {entry.nutritionInfo.calories} kcal
            </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalle - Optimizado para móvil */}
      <Dialog 
        open={isDetailModalOpen} 
        onClose={handleCloseDetail}
        fullScreen
        PaperProps={{
          sx: { borderRadius: 0 }
        }}
      >
        {selectedEntry && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pb: 1
            }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                Detalle del Plato
            </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={handleShare} color="primary" size="small">
                  <ShareIcon />
                </IconButton>
                <IconButton onClick={handleCloseDetail} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Imagen */}
                <Box sx={{ width: '100%', height: '300px' }}>
                  {selectedEntry.imageUrl && (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      borderRadius: 2, 
                      overflow: 'hidden', 
                      bgcolor: '#f4faef',
                      boxShadow: 2
                    }}>
                      <img
                        src={selectedEntry.imageUrl}
                        alt="Plato registrado"
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          display: 'block' 
                        }}
                      />
                    </Box>
                  )}
        </Box>

                {/* Información */}
                <Box>
                  {/* Fecha */}
                  <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                    {new Date(selectedEntry.date).toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>

                  {/* Alimentos detectados */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                      <RestaurantIcon color="primary" />
                      Alimentos Detectados
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selectedEntry.foods.map((food, index) => (
                      <Chip 
                          key={index}
                          label={capitalizeFirstLetter(food)} 
                          size="small" 
                        color="primary" 
                        variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                      />
                    ))}
                    </Stack>
                  </Box>

                  {/* Información nutricional */}
                  {selectedEntry.nutritionInfo && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Información Nutricional
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Calorías
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {selectedEntry.nutritionInfo.calories} kcal
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Proteínas
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {selectedEntry.nutritionInfo.protein}g
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Carbohidratos
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {selectedEntry.nutritionInfo.carbs}g
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                              Fibra
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {selectedEntry.nutritionInfo.fiber}g
        </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  {/* Información adicional */}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ID: {selectedEntry.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de alimentos: {selectedEntry.foods.length}
                    </Typography>
                  </Box>
                </Box>
      </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleShare} variant="outlined" color="primary" startIcon={<ShareIcon />}>
                Compartir
              </Button>
              <Button onClick={handleCloseDetail} variant="contained" color="primary">
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar para mensaje de compartir */}
      <Snackbar
        open={showShareMessage}
        autoHideDuration={3000}
        onClose={() => setShowShareMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowShareMessage(false)} severity="success" sx={{ width: '100%' }}>
          ¡Contenido copiado al portapapeles!
        </Alert>
      </Snackbar>
    </Container>
  );
}; 